import React from "react";
import BarChart from "../../../Components/GraphNCharts/BarChart";
import PieChart from "../../../Components/GraphNCharts/Pie";
import CustomTable from "../../../Components/MisReport/Table";
import NoDataFound from "../../../Components/Masters/NoDataFound";
import EditableReportSection from "./EditableReportSection";
import { truncateEmissionNumber } from "../../../utils/common";

const parseAnalysisText = (analysisText) => {
  const text = typeof analysisText === "string" ? analysisText.trim() : "";
  if (!text) return [];

  const matches = Array.from(
    text.matchAll(
      /(?:^|\n)\s*(\d+\.\s+[^\n]+)\n([\s\S]*?)(?=\n\s*\d+\.\s+[^\n]+\n|$)/g,
    ),
  );

  if (!matches.length) {
    return [{ title: "Analysis", content: text }];
  }

  return matches
    .map((match) => ({
      title: match[1].trim(),
      content: match[2].trim(),
    }))
    .filter((section) => section.title || section.content);
};

const normalizeAnalysisSections = (sections) => {
  if (!Array.isArray(sections)) return [];

  return sections.flatMap((section, index) => {
    if (typeof section === "string") {
      const parsedSections = parseAnalysisText(section);
      return parsedSections.length
        ? parsedSections
        : [{ title: `Analysis ${index + 1}`, content: section }];
    }

    if (!section || typeof section !== "object") return [];

    return [
      {
        title:
          section.title ||
          section.heading ||
          section.name ||
          `Analysis ${index + 1}`,
        content:
          section.content ||
          section.description ||
          section.text ||
          section.value ||
          "",
      },
    ].filter((item) => item.title || item.content);
  });
};

const formatSource = (source) => {
  if (typeof source === "string") return source;
  if (!source || typeof source !== "object") return "";

  return (
    source.name ||
    source.title ||
    source.source ||
    source.label ||
    source.value ||
    ""
  );
};

const ReportPageShell = ({
  coverStyles,
  vajraLogo,
  pageClassName = "",
  children,
}) => (
  <section className={`${coverStyles.report_page} ${pageClassName}`.trim()}>
    <img
      src={vajraLogo}
      alt="Vajra Logo"
      className={coverStyles.report_page_logo}
    />
    {children}
  </section>
);

const ReportSection = ({ coverStyles, title, children }) => (
  <div className={coverStyles.report_section_block}>
    <h2 className={coverStyles.report_section_title}>{title}</h2>
    <div className={coverStyles.report_section_body}>{children}</div>
  </div>
);

const ScopeContentSection = ({
  coverStyles,
  title,
  headers,
  rows,
  summaryText,
}) => (
  <div className={coverStyles.report_scope_section}>
    <h2 className={coverStyles.report_section_title}>{title}</h2>
    <div className={coverStyles.report_scope_table}>
      <CustomTable headers={headers} rows={rows} />
    </div>
    <div
      className={`${coverStyles.exec_highlight_box} ${coverStyles.scope_summary_box}`}
    >
      <p className={coverStyles.exec_list_item}>{summaryText}</p>
    </div>
  </div>
);

const GhgReportStyledPages = ({
  PageBreak,
  styles,
  coverStyles,
  vajraLogo,
  companyLogo,
  companyName,
  companyLogoSrc,
  data,
  user,
  summary,
  getReportingPeriodLabel,
  getExecutiveSummaryText,
  organizationalBoundaryText,
  organizationalBoundaryOriginal,
  onOrganizationalBoundarySave,
  operationalBoundaryText,
  operationalBoundaryOriginal,
  onOperationalBoundarySave,
  emissionFactorsText,
  emissionFactorsOriginal,
  onEmissionFactorsSave,
  quantificationMethodologyText,
  quantificationMethodologyOriginal,
  onQuantificationMethodologySave,
  dataAggregationText,
  dataAggregationOriginal,
  onDataAggregationSave,
  assumptionsText,
  assumptionsOriginal,
  onAssumptionsSave,
  limitationsText,
  limitationsOriginal,
  onLimitationsSave,
  analysisText,
  analysisOriginal,
  onAnalysisSave,
  verificationText,
  verificationOriginal,
  onVerificationSave,
  conclusionsText,
  conclusionsOriginal,
  onConclusionsSave,
  getTotalEmissions,
  currentYearDisplay,
  previousYearDisplay,
  scope1Headers,
  scope1Rows,
  scope2Rows,
  scope3Rows,
  scope1BarChartData,
  scope2BarChartData,
  scope3BarChartData,
  scope1Data,
  scope2Data,
  scope3Data,
  truncateNumber,
}) => {
  const year = data?.reportingYear || new Date().getFullYear();
  const totalEmissions = truncateEmissionNumber(getTotalEmissions());
  const stationaryCombustion =
    Number(data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal) || 0;

  const mobileCombustion =
    Number(data?.scope1Emissions?.mobileCombustion?.thisMonthTotal) || 0;

  const fugitiveCombustion =
    Number(data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal) || 0;

  const electricity =
    Number(data?.scope2Emissions?.electricity?.thisMonthTotal) || 0;

  const heatingCooling =
    Number(data?.scope2Emissions?.heat?.thisMonthTotal) || 0;

  const steam = Number(data?.scope2Emissions?.steam?.thisMonthTotal) || 0;

  const purchasedGoods =
    Number(data?.scope3Emissions?.purchasedGoods?.thisMonthTotal) || 0;

  const upstreamTransportation = Number(
    data?.scope3Emissions?.upstreamTransportation
      ?.upstreamTransportionDistribution ??
      data?.scope3Emissions?.upstreamAndDownStream
        ?.upstreamTransportionDistribution ??
      0,
  );

  const wasteGenerated =
    Number(data?.scope3Emissions?.wasteGenerated?.thisMonthTotal) || 0;

  const businessTravel =
    Number(data?.scope3Emissions?.businessTravel?.thisMonthTotal) || 0;

  const employeeCommute =
    Number(data?.scope3Emissions?.employeeCommute?.thisMonthTotal) || 0;

  const downstreamTransportation = Number(
    data?.scope3Emissions?.upstreamAndDownStream
      ?.downSteamTransportionDistribution ?? 0,
  );

  const scopePieTotals = [
    Number(data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0),
    Number(data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0),
    Number(data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0),
  ];
  const hasScopePieData = scopePieTotals.some((value) => value !== 0);

  const scope123Total =
    stationaryCombustion +
    mobileCombustion +
    fugitiveCombustion +
    electricity +
    heatingCooling +
    steam +
    purchasedGoods +
    upstreamTransportation +
    wasteGenerated +
    businessTravel +
    employeeCommute +
    downstreamTransportation;
  const organizationalBoundaryBullets = data?.organizationalBoundaryItems || [
    "Manufacturing Facility: Emissions arising from manufacturing activities including assembly, on-site fuel combustion, and other production-related operations.",
    "Direct Operational Control: Activities associated with facilities and assets owned or operated by the company, covering Scope 1 and Scope 2 emissions.",
    "Value Chain Activities: Scope 3 emissions including upstream and downstream activities such as purchased goods and services, business travel, employee commuting, and transportation and distribution.",
  ];

  const getGhgReportingText = () =>
    data?.executiveSummary?.ghgReporting || getExecutiveSummaryText();

  const getAnalysisSections = () => {
    const editableAnalysis = parseAnalysisText(analysisText);

    if (editableAnalysis.length) {
      return editableAnalysis;
    }
    const dynamicSections = normalizeAnalysisSections(
      data?.executiveSummary?.analysisSections || data?.analysisSections,
    );

    if (dynamicSections.length) {
      return dynamicSections;
    }

    const dynamicAnalysisText =
      data?.executiveSummary?.analysis || data?.analysis || data?.analysisText;
    const parsedAnalysis = parseAnalysisText(dynamicAnalysisText);

    if (parsedAnalysis.length) {
      return parsedAnalysis;
    }

    return [
      {
        title: "1. Emission Composition",
        content: `Scope 1 emissions contribute ${truncateEmissionNumber(data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0)} tCO2e, Scope 2 contributes ${truncateEmissionNumber(data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e, and Scope 3 contributes ${truncateEmissionNumber(data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e to the total inventory of ${totalEmissions} tCO2e.`,
      },
      {
        title: "2. Hotspot Concentration",
        content:
          "The highest contributing emission sources were identified by comparing category-level totals across Scope 1, Scope 2, and Scope 3 to prioritize reduction opportunities.",
      },
      {
        title: "3. Trend Analysis",
        content: `Year-on-year changes were assessed by comparing ${currentYearDisplay} inventory year data with ${previousYearDisplay} where historical records were available.`,
      },
      {
        title: "4. Intensity Analysis",
        content:
          "Emission intensity metrics such as emissions per unit of production or per employee were reviewed where relevant operational data was available.",
      },
      {
        title: "5. Boundary and Data Quality Interpretation",
        content:
          "Results were interpreted within the defined organizational and operational boundaries, with data quality reviewed for completeness and consistency.",
      },
      {
        title: "6. Scope 4 Separation",
        content:
          "Avoided emissions (Scope 4) are reported separately from the main GHG inventory and are not included in the total emissions figure presented in this report.",
      },
    ];
  };

  const getVerificationText = () =>
    verificationText ||
    data?.verificationAndValidation ||
    data?.verification ||
    `The inventory was not externally verified for the reporting period.`;

  const getVerificationBullets = () =>
    data?.verificationItems || [
      "Data Accuracy: Activity data and emission calculations were reviewed for completeness and consistency.",
      "Calculation Methodology: Emission factors, GWPs, and calculation procedures were checked against recognized standards.",
      "Documentation Review: Supporting records and assumptions were verified against submitted evidence.",
    ];

  const getConclusionFindings = () =>
    conclusionsText ||
    data?.executiveSummary?.summaryOfFindings ||
    `In ${year}, ${companyName} reported total GHG emissions of ${totalEmissions} tCO2e. Scope 3 represents a substantial share of the overall inventory, indicating value chain activities as key emission drivers. Scope 1 and Scope 2 contributions reflect direct operations and purchased energy consumption for the reporting period.`;

  const getConclusionRecommendations = () =>
    data?.executiveSummary?.recommendations ||
    `Recommend prioritizing emission reduction measures across high-contributing Scope 3 categories, improving data collection for categories with estimated values, and reviewing opportunities to reduce Scope 2 impacts through energy efficiency and renewable energy initiatives in the upcoming reporting year.`;

  const getAppendixSources = () => {
    const sourceList =
      data?.executiveSummary?.sources ||
      data?.sources ||
      (Array.isArray(data) ? data[0]?.sources : []);

    if (!Array.isArray(sourceList)) return [];

    return sourceList.map(formatSource).filter(Boolean);
    // return DEFAULT_APPENDIX_SOURCES;
  };

  const scope1SummaryText =
    data?.executiveSummary?.scope1Summary ||
    data?.scope1Summary ||
    summary?.scope1 ||
    `${companyName} Scope 1 emissions for ${currentYearDisplay} were ${truncateEmissionNumber(data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0)} tCO2e.`;

  const scope2SummaryText =
    data?.executiveSummary?.scope2Summary ||
    data?.scope2Summary ||
    summary?.scope2 ||
    `${companyName} Scope 2 emissions for ${currentYearDisplay} were ${truncateEmissionNumber(data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e.`;

  const scope3SummaryText =
    data?.executiveSummary?.scope3Summary ||
    data?.scope3Summary ||
    summary?.scope3 ||
    `${companyName} Scope 3 emissions for ${currentYearDisplay} were ${truncateEmissionNumber(data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0)} tCO2e.`;

  const currentYearLabel = `${currentYearDisplay} Inventory Year (tCO2e)`;
  const previousYearLabel = `${previousYearDisplay} Inventory Year (tCO2e)`;
  const scope4AvoidanceHeaders = [
    "Avoidance Source",
    currentYearLabel,
    previousYearLabel,
    "% Change",
  ];
  const scope4OffsetHeaders = [
    "Offset Source",
    currentYearLabel,
    previousYearLabel,
    "% Change",
  ];
  const scope4MetricRow = (sourceKey, label, metricData = {}) => ({
    [sourceKey]: label,
    [currentYearLabel]: truncateEmissionNumber(metricData?.thisMonthTotal ?? 0),
    [previousYearLabel]: truncateEmissionNumber(
      metricData?.previousMonthTotal ?? 0,
    ),
    "% Change": truncateEmissionNumber(metricData?.percentageChange ?? 0, 2),
  });
  const scope4AvoidanceRows = [
    scope4MetricRow(
      "Avoidance Source",
      "Avoidance from renewable energy Export",
      data?.scope4Emission?.renewable,
    ),
    scope4MetricRow(
      "Avoidance Source",
      "Low Carbon Product Use/ Cleaner Alternatives",
      data?.scope4Emission?.lowCarbon,
    ),
    scope4MetricRow(
      "Avoidance Source",
      "Total Scope 4 Carbon Avoidance",
      data?.scope4Emission?.avoidanceSourceSum,
    ),
  ];
  console.log("scope4AvoidanceRows", data.scope4Emission);
  const scope4OffsetRows = [
    scope4MetricRow(
      "Offset Source",
      "In-house Offset Projects-Carbon Sequestered",
      data?.scope4Emission?.inhouse,
    ),
    scope4MetricRow(
      "Offset Source",
      "Offset Credit Purchase Tracking Credit Purchased",
      data?.scope4Emission?.offsetting,
    ),
    scope4MetricRow(
      "Offset Source",
      "Total Scope 4 Carbon Offset",
      data?.scope4Emission?.offsetSourceSum,
    ),
  ];
  const scope4SummaryText =
    data?.executiveSummary?.scope4Summary ||
    data?.scope4Summary ||
    summary?.scope4 ||
    `${companyName} Scope 4 avoided and offset emissions for ${currentYearDisplay} were ${truncateEmissionNumber(data?.scope4Emission?.avoidanceSourceSum?.thisMonthTotal ?? 0)} tCO2e and ${truncateEmissionNumber(data?.scope4Emission?.offsetSourceSum?.thisMonthTotal ?? 0)} tCO2e respectively.`;
  const renderEmissionBreakdownTable = () => (
    <table
      className={`${styles.summary_table} ${coverStyles.report_emission_table}`}
    >
      <thead>
        <tr>
          <th className={styles.emission_table_header}>Emission Scope</th>
          <th className={styles.emission_table_header}>Emission Source</th>
          <th
            className={styles.emission_table_header}
            style={{ textAlign: "left" }}
          >
            Total Emission (tCO2e)
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={styles.emission_table_cell} rowSpan={3}>
            Scope 1
          </td>
          <td className={styles.emission_table_cell}>Stationary Combustion</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>Mobile Combustion</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>Fugitive Emission</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell} rowSpan={3}>
            Scope 2
          </td>
          <td className={styles.emission_table_cell}>Purchased Electricity</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>
            Purchased Heating and Cooling
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope2Emissions?.heat?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>Purchased Steam</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope2Emissions?.steam?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell} rowSpan={6}>
            Scope 3
          </td>
          <td className={styles.emission_table_cell}>
            Purchased Goods &amp; Services
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>
            Upstream Transportation and Distribution
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.upstreamTransportation
                ?.upstreamTransportionDistribution ??
                data?.scope3Emissions?.upstreamAndDownStream
                  ?.upstreamTransportionDistribution ??
                0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>
            Waste Generated in operations
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>Business Travel</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.businessTravel?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>Employee Commute</td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.employeeCommute?.thisMonthTotal ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell}>
            Downstream Transportation and Distribution
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left" }}
          >
            {truncateEmissionNumber(
              data?.scope3Emissions?.upstreamAndDownStream
                ?.downSteamTransportionDistribution ?? 0,
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.emission_table_cell} colSpan={2}>
            <strong>Total Emissions</strong>
          </td>
          <td
            className={styles.emission_table_cell}
            style={{ textAlign: "left", fontWeight: 700 }}
          >
            {truncateEmissionNumber(scope123Total)}
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <>
      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="2. Organizational Boundary"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={organizationalBoundaryText}
            originalValue={organizationalBoundaryOriginal}
            onSave={onOrganizationalBoundarySave}
          />
          {/* <ul className={coverStyles.report_bullet_list}>
            {organizationalBoundaryBullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul> */}
        </div>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="3. Operational Boundary"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={operationalBoundaryText}
            originalValue={operationalBoundaryOriginal}
            onSave={onOperationalBoundarySave}
          />
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="4. Emission Factors & Data Sources"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={emissionFactorsText}
            originalValue={emissionFactorsOriginal}
            onSave={onEmissionFactorsSave}
          />
        </div>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="5. GHG Quantification Methodology"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={quantificationMethodologyText}
            originalValue={quantificationMethodologyOriginal}
            onSave={onQuantificationMethodologySave}
          />
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="6. GHG Data Aggregation And Reporting Period"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={dataAggregationText}
            originalValue={dataAggregationOriginal}
            onSave={onDataAggregationSave}
          />
        </div>
        <ReportSection coverStyles={coverStyles} title="7. GHG Reporting">
          <p>{getGhgReportingText()}</p>
        </ReportSection>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        {renderEmissionBreakdownTable()}
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_chart_row}>
          <div className={coverStyles.report_chart_table}>
            <table className={styles.summary_table}>
              <thead>
                <tr>
                  <th className={styles.summary_table_header}>{companyName}</th>
                  <th className={styles.summary_table_header}>
                    Emission (tCO2e)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.summary_table_cell}>Scope 1</td>
                  <td
                    className={styles.summary_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateEmissionNumber(
                      data?.scope1Emissions?.totalScope1Combustion
                        ?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.summary_table_cell}>Scope 2</td>
                  <td
                    className={styles.summary_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateEmissionNumber(
                      data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.summary_table_cell}>Scope 3</td>
                  <td
                    className={styles.summary_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateEmissionNumber(
                      data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.summary_table_cell}>Scope 1 + 2</td>
                  <td
                    className={styles.summary_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateEmissionNumber(
                      Number(
                        data?.scope1Emissions?.totalScope1Combustion
                          ?.thisMonthTotal ?? 0,
                      ) +
                        Number(
                          data?.scope2Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        ),
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.summary_table_cell}>Scope 1 + 2 + 3</td>
                  <td
                    className={styles.summary_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateEmissionNumber(scope123Total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={coverStyles.report_chart_pie}>
            {hasScopePieData ? (
              <PieChart
                labels={["Scope 1", "Scope 2", "Scope 3"]}
                dataset={[
                  {
                    data: scopePieTotals,
                    backgroundColor: ["#FF4C4C", "#007BFF", "#eb7e0e"],
                    borderColor: "#ffffff",
                    borderWidth: 2,
                  },
                ]}
                customWidth={325}
                customHeight={270}
                displayLegend={true}
                showOutsidePercentages={true}
              />
            ) : (
              <div
                style={{
                  width: 325,
                  height: 270,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <NoDataFound mt={0} />
              </div>
            )}
          </div>
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_scopes_stack}>
          <ScopeContentSection
            coverStyles={coverStyles}
            title="Scope 1: Direct Emissions"
            headers={scope1Headers}
            rows={scope1Rows}
            summaryText={scope1SummaryText}
          />
          <div className={coverStyles.report_scope_chart}>
            <BarChart
              labels={[
                "Stationary Combustion",
                "Mobile Combustion",
                "Fugitive Emission",
              ]}
              datasets={[
                {
                  label: `${currentYearDisplay}`,
                  data: [
                    Number(
                      data?.scope1Emissions?.stationaryCombustion
                        ?.thisMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope1Emissions?.fugitiveCombustion
                        ?.thisMonthTotal ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(255, 79, 79, 0.8)",
                  borderColor: "rgba(255, 79, 79, 1)",
                  barThickness: 30,
                },
                {
                  label: `${previousYearDisplay}`,
                  data: [
                    Number(
                      data?.scope1Emissions?.stationaryCombustion
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope1Emissions?.mobileCombustion
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope1Emissions?.fugitiveCombustion
                        ?.previousMonthTotal ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(14, 98, 224, 0.8)",
                  borderColor: "rgba(14, 98, 224, 1)",
                  barThickness: 30,
                },
              ]}
              ellipsis={false}
              display={true}
              yAxisTitle={"Scope 1 Emission (tCO2e)"}
            />
          </div>
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_scopes_stack}>
          <ScopeContentSection
            coverStyles={coverStyles}
            title="Scope 2: Indirect Emissions from purchased energy"
            headers={scope1Headers}
            rows={scope2Rows}
            summaryText={scope2SummaryText}
          />
          <div className={coverStyles.report_scope_chart}>
            <BarChart
              labels={["Electricity", "Heating and Cooling", "Steam"]}
              datasets={[
                {
                  label: `${currentYearDisplay}`,
                  data: [
                    Number(
                      data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
                    ),
                    Number(data?.scope2Emissions?.heat?.thisMonthTotal ?? 0),
                    Number(data?.scope2Emissions?.steam?.thisMonthTotal ?? 0),
                  ],
                  backgroundColor: "rgba(255, 79, 79, 0.8)",
                  borderColor: "rgba(255, 79, 79, 1)",
                  barThickness: 30,
                },
                {
                  label: `${previousYearDisplay}`,
                  data: [
                    Number(
                      data?.scope2Emissions?.electricity?.previousMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope2Emissions?.heat?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope2Emissions?.steam?.previousMonthTotal ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(14, 98, 224, 0.8)",
                  borderColor: "rgba(14, 98, 224, 1)",
                  barThickness: 30,
                },
              ]}
              ellipsis={false}
              display={true}
              yAxisTitle={"Scope 2 Emission (tCO2e)"}
            />
          </div>
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_scopes_stack}>
          <ScopeContentSection
            coverStyles={coverStyles}
            title="Scope 3: Emissions from Value Chain"
            headers={scope1Headers}
            rows={scope3Rows}
            summaryText={scope3SummaryText}
          />
          <div className={coverStyles.report_scope_chart}>
            <BarChart
              labels={[
                "Purchased Goods",
                "Upstream Transportation",
                "Waste Generated",
                "Business Travel",
                "Employee Commute",
                "Downstream Transportation",
              ]}
              datasets={[
                {
                  label: `${currentYearDisplay}`,
                  data: [
                    Number(
                      data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.upstreamTransportation
                        ?.upstreamTransportionDistribution ??
                        data?.scope3Emissions?.upstreamAndDownStream
                          ?.upstreamTransportionDistribution ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.businessTravel?.thisMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.employeeCommute?.thisMonthTotal ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.upstreamAndDownStream
                        ?.downSteamTransportionDistribution ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(255, 79, 79, 0.8)",
                  borderColor: "rgba(255, 79, 79, 1)",
                  barThickness: 30,
                },
                {
                  label: `${previousYearDisplay}`,
                  data: [
                    Number(
                      data?.scope3Emissions?.purchasedGoods
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope3Emissions?.upstreamTransportation
                        ?.prevUpstreamTransportionDistribution ??
                        data?.scope3Emissions?.upstreamAndDownStream
                          ?.prevUpstreamTransportionDistribution ??
                        0,
                    ),
                    Number(
                      data?.scope3Emissions?.wasteGenerated
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope3Emissions?.businessTravel
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope3Emissions?.employeeCommute
                        ?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope3Emissions?.upstreamTransportation
                        ?.prevDownSteamTransportionDistribution ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(14, 98, 224, 0.8)",
                  borderColor: "rgba(14, 98, 224, 1)",
                  barThickness: 30,
                },
              ]}
              ellipsis={false}
              display={true}
              yAxisTitle={"Scope 3 Emission (tCO2e)"}
            />
          </div>
        </div>
      </ReportPageShell>
      {/* <PageBreak /> */}
      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_scopes_stack}>
          <div className={coverStyles.report_scope_section}>
            <h2 className={coverStyles.report_section_title}>
              Scope 4 : Avoided Emissions and Carbon Offsetting
            </h2>
            <div className={coverStyles.report_scope_table}>
              <CustomTable
                headers={scope4AvoidanceHeaders}
                rows={scope4AvoidanceRows}
              />
            </div>
            <div className={coverStyles.report_scope_table}>
              <CustomTable
                headers={scope4OffsetHeaders}
                rows={scope4OffsetRows}
              />
            </div>
            <div
              className={`${coverStyles.exec_highlight_box} ${coverStyles.scope_summary_box}`}
            >
              <p className={coverStyles.exec_list_item}>{scope4SummaryText}</p>
            </div>
          </div>
          <div className={coverStyles.report_scope_chart}>
            <BarChart
              labels={[
                "Avoidance from Renewable Energy Export",
                "Low-carbon Product Use",
                "In-house Offset Projects",
                "Offset Credit Purchase Tracking",
              ]}
              datasets={[
                {
                  label: `${currentYearDisplay}`,
                  data: [
                    Number(
                      data?.scope4Emission?.renewable?.thisMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope4Emission?.lowCarbon?.thisMonthTotal ?? 0,
                    ),
                    Number(data?.scope4Emission?.inhouse?.thisMonthTotal ?? 0),
                    Number(
                      data?.scope4Emission?.offsetting?.thisMonthTotal ?? 0,
                    ),
                  ],
                  backgroundColor: "rgba(255, 79, 79, 0.8)",
                  borderColor: "rgba(255, 79, 79, 1)",
                  barThickness: 30,
                },
                {
                  label: `${previousYearDisplay}`,
                  data: [
                    Number(
                      data?.scope4Emission?.renewable?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope4Emission?.lowCarbon?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope4Emission?.inhouse?.previousMonthTotal ?? 0,
                    ),
                    Number(
                      data?.scope4Emission?.offsetting?.previousMonthTotal ??
                        0,
                    ),
                  ],
                  backgroundColor: "rgba(14, 98, 224, 0.8)",
                  borderColor: "rgba(14, 98, 224, 1)",
                  barThickness: 30,
                },
              ]}
              ellipsis={false}
              display={true}
              yAxisTitle={"Scope 4 Emission (tCO2e)"}
            />
          </div>
        </div>
      </ReportPageShell>

      {data?.typeOfReport === "Consolidated" ? (
        <>
          {/* <PageBreak /> */}
          <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
            <h2 className={coverStyles.report_section_title}>
              Facility-wise Analytics
            </h2>
            <p className={coverStyles.report_subheading}>
              Bar chart showing Scope 1 of current and previous inventory year
              emissions
            </p>
            <BarChart
              labels={scope1BarChartData?.map((item) => item?.facility)}
              datasets={scope1Data}
              display={true}
              yAxisTitle={"Scope 1 Emission (tCO2e)"}
            />
            <p className={coverStyles.exec_list_item}>
              {data?.scope1Summary || summary?.scope1 || ""}
            </p>
            <p
              className={coverStyles.report_subheading}
              style={{ marginTop: "2rem" }}
            >
              Bar chart showing Scope 2 of current and previous emissions
            </p>
            <BarChart
              labels={scope2BarChartData?.map((item) => item?.facility)}
              datasets={scope2Data}
              display={true}
              yAxisTitle={"Scope 2 Emission (tCO2e)"}
            />
            <p className={coverStyles.exec_list_item}>
              {data?.scope2Summary || summary?.scope2 || ""}
            </p>
            <p
              className={coverStyles.report_subheading}
              style={{ marginTop: "2rem" }}
            >
              Bar chart showing Scope 3 of current and previous emissions
            </p>
            <BarChart
              labels={scope3BarChartData?.map((item) => item?.facility)}
              datasets={scope3Data}
              display={true}
              yAxisTitle={"Scope 3 Emission (tCO2e)"}
            />
            <p className={coverStyles.exec_list_item}>
              {data?.scope3Summary || summary?.scope3 || ""}
            </p>
          </ReportPageShell>
        </>
      ) : null}

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="8. Assumptions"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            displayAsList
            listClassName={coverStyles.report_bullet_list}
            value={assumptionsText}
            originalValue={assumptionsOriginal}
            onSave={onAssumptionsSave}
          />
        </div>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="9. Limitations"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={limitationsText}
            originalValue={limitationsOriginal}
            onSave={onLimitationsSave}
          />
        </div>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="10. Analysis"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={analysisText}
            originalValue={analysisOriginal}
            onSave={onAnalysisSave}
            renderValue={() =>
              getAnalysisSections().map((section) => (
                <div
                  key={section.title}
                  className={coverStyles.report_analysis_item}
                >
                  <h3 className={coverStyles.report_analysis_title}>
                    {section.title}
                  </h3>
                  <p>{section.content}</p>
                </div>
              ))
            }
          />
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell coverStyles={coverStyles} vajraLogo={vajraLogo}>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="11. Verification And Validation"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={getVerificationText()}
            originalValue={verificationOriginal}
            onSave={onVerificationSave}
          />
        </div>
        <div className={coverStyles.report_section_block}>
          <EditableReportSection
            title="12. Conclusions"
            titleClassName={coverStyles.report_section_title}
            showColorBar={false}
            value={getConclusionFindings()}
            originalValue={conclusionsOriginal}
            onSave={onConclusionsSave}
            renderValue={(value) => (
              <div className={coverStyles.exec_highlight_box}>
                <h3 className={coverStyles.exec_highlight_title}>
                  Summary of key findings
                </h3>
                <p className={coverStyles.exec_list_item}>{value}</p>
              </div>
            )}
          />
        </div>
      </ReportPageShell>

      {/* <PageBreak /> */}

      <ReportPageShell
        coverStyles={coverStyles}
        vajraLogo={vajraLogo}
        pageClassName={coverStyles.appendix_page}
      >
        <div className={coverStyles.appendix_box}>
          <h2 className={coverStyles.appendix_title}>Appendices</h2>
          <p className={coverStyles.appendix_subtitle}>
            Appendix A: Emission Source
          </p>
          <ol className={coverStyles.appendix_list}>
            {getAppendixSources().map((source, index) => (
              <li key={`${source}-${index}`}>{source}</li>
            ))}
          </ol>
        </div>
        <div className={coverStyles.report_powered_footer} id="company_footer">
          <span className={coverStyles.report_powered_text}>Powered by -</span>
          <img
            src={companyLogo}
            alt="Vajra"
            className={coverStyles.report_powered_logo}
          />
        </div>
      </ReportPageShell>
    </>
  );
};

export default GhgReportStyledPages;
